/** @format */

import React, { useState, useMemo } from 'react';
import { Table, Input, Button, Space, Spin, Empty } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ListedLeadLog, ListedLeadsLogFilterInput } from '../types/listedLeadsLog.types';
import { useListedLeadsLogs } from '../hooks/useListedLeadsLogs';
import formatCurrency from 'utils/formatCurrency';

interface ListedLeadsLogTableProps {
  filter?: ListedLeadsLogFilterInput;
  refreshTrigger?: number;
  onRefresh?: () => void;
}

const ListedLeadsLogTable: React.FC<ListedLeadsLogTableProps> = ({
  filter,
  refreshTrigger = 0,
  onRefresh,
}) => {
  const { data, totalCost, loading, error } = useListedLeadsLogs(filter, refreshTrigger);
  const [searchText, setSearchText] = useState('');
  const [pageSize, setPageSize] = useState(50);

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchText) return data;

    return data.filter((log) => {
      const searchLower = searchText.toLowerCase();
      return (
        log.listId?.toLowerCase().includes(searchLower) ||
        log.firstName?.toLowerCase().includes(searchLower) ||
        log.lastName?.toLowerCase().includes(searchLower) ||
        log.phoneNumber?.includes(searchLower)
      );
    });
  }, [data, searchText]);

  const columns: ColumnsType<ListedLeadLog> = [
    {
      title: 'List ID',
      dataIndex: 'listId',
      key: 'listId',
      sorter: (a, b) => (a.listId || '').localeCompare(b.listId || ''),
      width: 120,
    },
    {
      title: 'First Name',
      dataIndex: 'firstName',
      key: 'firstName',
      sorter: (a, b) => (a.firstName || '').localeCompare(b.firstName || ''),
      width: 120,
    },
    {
      title: 'Last Name',
      dataIndex: 'lastName',
      key: 'lastName',
      sorter: (a, b) => (a.lastName || '').localeCompare(b.lastName || ''),
      width: 120,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 120,
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      sorter: (a, b) => a.cost - b.cost,
      render: (cost: number) => formatCurrency(cost),
      width: 100,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      render: (date: string | Date) => new Date(date).toLocaleDateString(),
      width: 120,
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <Input
          placeholder='Search by List ID, First Name, Last Name, or Phone'
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ flex: 1, maxWidth: '400px' }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </div>

      <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '14px' }}>
        <strong>Total Cost: {formatCurrency(totalCost)}</strong>
      </div>

      <Spin spinning={loading}>
        {filteredData.length === 0 ? (
          <Empty description='No listed lead logs found' />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            pagination={{
              pageSize,
              pageSizeOptions: ['25', '50', '100', '200'],
              onShowSizeChange: (_, size) => setPageSize(size),
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} logs`,
            }}
            scroll={{ x: 800 }}
          />
        )}
      </Spin>
    </div>
  );
};

export default ListedLeadsLogTable;
